'use client'

import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePathname, useRouter } from 'next/navigation'
import { FaGlobeAmericas, FaCaretDown } from 'react-icons/fa'
import Cookies from 'js-cookie' // Import js-cookie
import {
  LOCALIZED_PATHS,
  localizedPath,
  stripLocalePrefix,
} from '../app/i18n/supported-locales'

function LanguageSwitcher() {
  const { i18n } = useTranslation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)
  const pathname = usePathname()
  const router = useRouter()

  // On component mount, check if a language cookie exists, if so set that as the i18n language
  useEffect(() => {
    const savedLanguage = Cookies.get('language')
    if (savedLanguage) {
      i18n.changeLanguage(savedLanguage)
    }
  }, [i18n])

  const changeLanguage = (language) => {
    i18n.changeLanguage(language)
    if (typeof document !== 'undefined')
      document.documentElement.lang = language
    setDropdownOpen(false)
    Cookies.set('language', language, { expires: 365 }) // Save the language preference in a cookie, here for 1 year
    try {
      localStorage.setItem('language', language)
    } catch {}

    // SEO pages have real per-language URLs (/de, /de/nsfw-ai, …) whose body is
    // server-rendered in that language — navigate so the WHOLE page switches, not just the
    // client-side t() strings. The landing experiment is already fully translated in place;
    // sending an assigned B/C visitor to /de would replace their variant with the baseline
    // while retaining B/C attribution, so keep experiment participants on the assigned page.
    const basePath = stripLocalePrefix(pathname)
    const isLandingExperiment =
      typeof document !== 'undefined' &&
      Boolean(document.querySelector('[data-landing-redesign]'))
    if (LOCALIZED_PATHS.has(basePath) && !isLandingExperiment) {
      const target = localizedPath(basePath, language)
      if (target !== pathname) router.push(target)
    }
  }
  return (
    <div className="relative inline-block text-left">
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-label="Language Selector Dropdown"
        className="flex items-center px-3 py-3 bg-[var(--foreground-bg-color)] text-white rounded hover:bg-[var(--secondary-bg-color)]"
      >
        <FaGlobeAmericas className="mr-1" />

        <FaCaretDown size={12} />
      </button>
      {dropdownOpen && (
        <div
          ref={dropdownRef}
          className="origin-top-right absolute right-0 mt-2 bg-[var(--foreground-bg-color)] text-white rounded-md shadow-lg z-10 w-64"
        >
          <div
            role="menu"
            aria-orientation="vertical"
            aria-labelledby="options-menu"
            className="grid grid-cols-2 gap-1"
          >
            <button
              onClick={() => changeLanguage('en')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              English
            </button>
            <button
              onClick={() => changeLanguage('hi')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              हिंदी
            </button>
            <button
              onClick={() => changeLanguage('ja')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              日本語
            </button>
            <button
              onClick={() => changeLanguage('es')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              Español
            </button>
            <button
              onClick={() => changeLanguage('de')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              Deutsch
            </button>
            <button
              onClick={() => changeLanguage('pt')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              Português
            </button>
            <button
              onClick={() => changeLanguage('it')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              Italiano
            </button>
            <button
              onClick={() => changeLanguage('ko')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              한국어
            </button>
            <button
              onClick={() => changeLanguage('zh-CN')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              简体中文
            </button>
            <button
              onClick={() => changeLanguage('fr')}
              className="block px-4 py-2 text-sm text-white hover:bg-[var(--secondary-bg-color)] w-full"
            >
              Français
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default LanguageSwitcher
